import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppNavigate } from "../lib/navigate";
import {
  Apple,
  Beef,
  Candy,
  ChevronDown,
  ClipboardList,
  Coffee,
  Fish,
  Heart,
  MessageCircle,
  Milk,
  Minus,
  Package,
  Plus,
  Search,
  ShoppingBasket,
  ShoppingCart,
  Sparkles,
  Tag,
  User,
  Wine,
  X,
} from "lucide-react";
import { DynamicIcon, dynamicIconImports } from "lucide-react/dynamic";
import api from "../lib/api";
import { applyDocumentMetadata } from "../lib/metadata";
import { Drawer, DrawerContent } from "../components/ui/drawer";
import { DrawerProductDetail } from "../components/product/DrawerProductDetail";
import { DrawerInCart } from "../components/product/DrawerInCart";
import { DrawerCart } from "../components/product/DrawerCart";
import AuthDrawer from "../components/AuthDrawer.jsx";
import { resolveToken, savePersistentToken, removeSessionToken } from "../lib/tokenStorage.js";
import { loadCart, saveCart } from "../lib/cartStorage";
import {
  buildCartLineId,
  buildOptionSummaries,
  cloneOptionValues,
  formatCurrency,
  getProductImage,
  hexToRgba,
  isConfigurableProduct,
  normalizeHexColor,
  normalizeProductOptions,
  resolveProductPriceInfo,
  resolveMaxQuantity,
  resolveProductStartingPrice,
  resolveProductUnitInfo,
  resolveUnitPriceForSelection,
  serializeOptionValues,
} from "../lib/productUtils";
import productPlaceholder from "../assets/product-placeholder.svg";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const logoFallbackUrl = "https://www.figma.com/api/mcp/asset/03690a11-0114-4a29-badc-4359c4a71123";

const DEFAULT_BRANDING = {
  appName: "My PingUp",
  logoUrl: "",
  primaryColor: "#38bdf8",
  accentColor: "#f472b6",
  subtitle: "",
  accentTitle: "",
};

const DEFAULT_WEBAPP_DESIGN = {
  layout: "standard",
};

const CATEGORY_ICON_MAP = {
  apple: Apple,
  banana: Apple,
  beef: Beef,
  meat: Beef,
  candy: Candy,
  sweets: Candy,
  coffee: Coffee,
  fish: Fish,
  seafood: Fish,
  heart: Heart,
  milk: Milk,
  dairy: Milk,
  package: Package,
  product: Package,
  "shopping-basket": ShoppingBasket,
  "shopping-cart": ShoppingBasket,
  basket: ShoppingBasket,
  sparkle: Sparkles,
  sparkles: Sparkles,
  star: Sparkles,
  wine: Wine,
};

// ---------------------------------------------------------------------------
// Home-specific helpers
// ---------------------------------------------------------------------------

function normalizeIconToken(icon) {
  return String(icon || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[_\s]+/g, "-")
    .toLowerCase();
}

function CategoryChipIcon({ icon, color }) {
  if (!icon) return null;
  const normalized = normalizeIconToken(icon);
  const IconComponent = CATEGORY_ICON_MAP[normalized];
  if (IconComponent) {
    return <IconComponent className="h-4 w-4" aria-hidden="true" style={{ color }} />;

  }

  if (normalized in dynamicIconImports) {
    return <DynamicIcon name={normalized} className="h-4 w-4" aria-hidden="true" />;
  }

  return <Tag className="h-4 w-4" aria-hidden="true" />;
}

function initialsFromCustomer(customer) {
  const tokens = [customer?.name, customer?.surname]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .slice(0, 2);

  if (!tokens.length) return "U";
  return tokens.map((token) => token[0]?.toUpperCase() || "").join("");
}

function toWhatsAppLink(phone) {
  const digits = String(phone || "").replace(/\D+/g, "");
  return digits ? `https://wa.me/${digits}` : "";
}

// ---------------------------------------------------------------------------
// Banner components
// ---------------------------------------------------------------------------

function formatBannerExpiry(isoDate) {
  if (!isoDate) return null;
  try {
    return new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", year: "numeric" }).format(new Date(isoDate));
  } catch {
    return null;
  }
}

function BannerCard({ banner, primaryColor, tenantId, navigate, onShowPromotion }) {
  const [copied, setCopied] = useState(false);

  function handleCta(e) {
    e.stopPropagation();
    if (banner.ctaType === "discount_code" && banner.ctaDiscountCode) {
      navigator.clipboard?.writeText(banner.ctaDiscountCode).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else if (banner.ctaType === "link" && banner.ctaLink) {
      const href = String(banner.ctaLink);
      if (href.startsWith("http://") || href.startsWith("https://")) {
        window.open(href, "_blank", "noopener noreferrer");
      } else {
        navigate(href);
      }
    } else if (
      banner.ctaType === "promotion_products" &&
      banner.linkedPromotion?.conditionType === "product"
    ) {
      onShowPromotion?.(banner.linkedPromotion);
    }
  }

  const hasGradient = banner.bgType === "gradient" && banner.bgGradientTo;
  const bg = hasGradient
    ? `linear-gradient(135deg, ${banner.bgColor || "#1e293b"}, ${banner.bgGradientTo})`
    : banner.bgColor || "#1e293b";
  const hasCta = banner.ctaType !== "none";
  const ctaLabel = copied ? "Copiato!" : (banner.ctaLabel || "Scopri");
  const expiry = formatBannerExpiry(banner.validTo);

  if (banner.type === "image" && banner.imageUrl) {
    return (
      <div className="relative overflow-hidden" style={{ background: banner.bgColor || "#1e293b", minHeight: "3.5rem" }}>
        <img
          src={banner.imageUrl}
          alt={banner.title || ""}
          className="block w-full"
          style={{ maxHeight: "250px", objectFit: "cover" }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            {banner.caption && (
              <p className="mb-0.5 text-[9px] font-bold uppercase tracking-widest text-white/70">{banner.caption}</p>
            )}
            {banner.title && (
              <p className="truncate text-sm font-bold text-white drop-shadow">{banner.title}</p>
            )}
            {banner.subtitle && (
              <p className="truncate text-xs text-white/80">{banner.subtitle}</p>
            )}
            {expiry && (
              <p className="mt-0.5 text-[10px] text-white/50">Fino al {expiry}</p>
            )}
          </div>
          {hasCta && (
            <button
              type="button"
              onClick={handleCta}
              className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-bold text-black transition-opacity active:opacity-70"
            >
              {ctaLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (banner.template === "bold") {
    return (
      <div
        className="flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center text-white"
        style={{ background: bg }}
      >
        {banner.caption && (
          <p className="text-[9px] font-bold uppercase tracking-widest opacity-70">{banner.caption}</p>
        )}
        {banner.title && (
          <p className="text-2xl font-black leading-tight tracking-tight">{banner.title}</p>
        )}
        {banner.subtitle && <p className="text-sm opacity-80">{banner.subtitle}</p>}
        {expiry && <p className="mt-1 text-[11px] opacity-50">Fino al {expiry}</p>}
        {hasCta && (
          <button
            type="button"
            onClick={handleCta}
            className="mt-1 rounded-full bg-white px-5 py-1.5 text-sm font-bold text-black transition-opacity active:opacity-70"
          >
            {ctaLabel}
          </button>
        )}
      </div>
    );
  }

  if (banner.template === "discount") {
    return (
      <div className="flex flex-col items-center justify-center px-5 py-6 text-center text-white" style={{ background: bg }}>
        {banner.caption && (
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest opacity-70">{banner.caption}</p>
        )}
        {banner.title && <p className="text-2xl font-black leading-tight">{banner.title}</p>}
        {banner.subtitle && <p className="mt-1 text-base opacity-80">{banner.subtitle}</p>}
        {banner.ctaType === "discount_code" && banner.ctaDiscountCode ? (
          <div className="mt-3 flex items-center gap-2">
            <code className="rounded-lg bg-white/15 px-4 py-2 font-mono text-base font-bold tracking-widest text-white">
              {banner.ctaDiscountCode}
            </code>
            <button
              type="button"
              onClick={handleCta}
              className="rounded-full bg-white px-4 py-2 text-sm font-bold text-black transition-opacity active:opacity-70"
            >
              {ctaLabel}
            </button>
          </div>
        ) : hasCta ? (
          <button
            type="button"
            onClick={handleCta}
            className="mt-3 rounded-full bg-white px-5 py-2 text-sm font-bold text-black transition-opacity active:opacity-70"
          >
            {ctaLabel}
          </button>
        ) : null}
      </div>
    );
  }

  // default: minimal
  return (
    <div className="flex items-center gap-3 px-4 py-3 text-white" style={{ background: bg }}>
      <div className="h-10 w-1 shrink-0 rounded-full bg-white/60" />
      <div className="min-w-0 flex-1">
        {banner.caption && (
          <p className="mb-0.5 text-[9px] font-bold uppercase tracking-widest opacity-60">{banner.caption}</p>
        )}
        {banner.title && (
          <p className="truncate text-sm font-bold leading-tight">{banner.title}</p>
        )}
        {banner.subtitle && (
          <p className="mt-0.5 truncate text-xs opacity-70">{banner.subtitle}</p>
        )}
        {expiry && (
          <p className="mt-0.5 truncate text-[10px] opacity-50">Fino al {expiry}</p>
        )}
      </div>
      {hasCta && (
        <button
          type="button"
          onClick={handleCta}
          className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-bold text-black transition-opacity active:opacity-70"
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

function BannerCarousel({ banners, primaryColor, tenantId, navigate, onShowPromotion }) {
  const [idx, setIdx] = useState(0);
  const touchStartRef = useRef(null);
  const touchStartYRef = useRef(null);
  const lastManualSwipeRef = useRef(0);
  const count = banners.length;

  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => {
      if (Date.now() - lastManualSwipeRef.current < 2500) return;
      setIdx((i) => (i + 1) % count);
    }, 4500);
    return () => clearInterval(id);
  }, [count]);

  function handleTouchStart(e) {
    touchStartRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  }

  function handleTouchEnd(e) {
    if (touchStartRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current;
    const deltaY = e.changedTouches[0].clientY - (touchStartYRef.current ?? e.changedTouches[0].clientY);
    touchStartRef.current = null;
    touchStartYRef.current = null;

    // Ignore vertical gestures so the page scroll feels natural.
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (Math.abs(deltaX) < 48) return;

    lastManualSwipeRef.current = Date.now();

    // Keep swipe direction aligned with the visual expectation.
    if (deltaX < 0) setIdx((i) => (i - 1 + count) % count);
    else setIdx((i) => (i + 1) % count);
  }

  return (
      <div className="py-3" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div className="overflow-hidden">
        <div
          className="flex items-stretch transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {banners.map((b) => (
            <div key={b._id} className="min-w-full flex flex-col px-4">
              {/* display:grid forces single child to stretch full height */}
              <div className="flex-1 rounded-xl overflow-hidden grid">
                <BannerCard banner={b} primaryColor={primaryColor} tenantId={tenantId} navigate={navigate} onShowPromotion={onShowPromotion} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {count > 1 && (
        <div className="flex justify-center gap-1.5 pb-1 pt-2">
          {banners.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className="h-1.5 rounded-full transition-all duration-200"
              style={{
                width: i === idx ? "16px" : "6px",
                background: i === idx ? primaryColor : `${primaryColor}55`,
              }}
              aria-label={`Banner ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function getDiscountPercent(product) {
  const info = resolveProductPriceInfo(product);
  if (!info.hasDiscount || info.original <= 0) return 0;
  return Math.round(((info.original - info.current) / info.original) * 100);
}

function ProductCard({
  product,
  primaryColor,
  inventoryManagement,
  productQuantityMap,
  onOpen,
  onAdd,
  onDecrease,
  featured = false,
}) {
  const productId = String(product?._id || "");
  const imageUrl = getProductImage(product, productPlaceholder) || productPlaceholder;
  const rowQuantity = productQuantityMap[productId] || 0;
  const configurable = isConfigurableProduct(product);
  const maxQty = resolveMaxQuantity(product, inventoryManagement);
  const soldOut = maxQty === 0;
  const maxReached = Number.isFinite(maxQty) && rowQuantity >= maxQty;
  const priceInfo = resolveProductPriceInfo(product);
  const discountPercent = getDiscountPercent(product);
  const hasPlaceholder = imageUrl === productPlaceholder;

  return (
    <article
      className={`flex cursor-pointer flex-col overflow-hidden rounded-xl border border-[#dfe6ee] bg-white shadow-sm transition-colors active:bg-[#f8fafc] ${featured ? "min-w-[180px] max-w-[180px]" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(product)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(product);
        }
      }}
    >
      <div className="relative flex h-[210px] max-h-[250px] items-center justify-center overflow-hidden bg-white">
        <img
          src={imageUrl}
          alt={product?.name || "Prodotto"}
          className={`h-full max-h-[250px] w-full ${hasPlaceholder ? "object-contain p-8 opacity-70" : "object-cover"}`}
          loading="lazy"
        />
        {discountPercent > 0 ? (
          <span className="absolute left-2 top-2 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white">
            -{discountPercent}%
          </span>
        ) : null}
        {soldOut ? (
          <span className="absolute right-2 top-2 rounded-full bg-[#f8d7da] px-2 py-0.5 text-[10px] font-semibold text-[#b42318]">
            Esaurito
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-3">
        <p className="line-clamp-2 min-h-[40px] text-sm font-semibold leading-5 text-[#0f172a]">
          {product?.name || "Prodotto"}
        </p>
        {product?.description ? (
          <p className="mt-1 line-clamp-2 text-xs leading-4 text-[#64748b]">{product.description}</p>
        ) : null}

        <div className="mt-auto pt-3">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              {priceInfo.hasDiscount ? (
                <p className="text-[11px] text-[#94a3b8] line-through">{priceInfo.originalLabel}</p>
              ) : null}
              <p className="text-sm font-bold text-[#0f172a]">{priceInfo.currentLabel}</p>
            </div>

            {soldOut ? (
              <span className="inline-flex h-8 items-center rounded-full border border-[#e2e8f0] px-3 text-xs text-[#64748b]">0</span>
            ) : configurable ? (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onAdd(product); }}
                className="inline-flex h-8 shrink-0 items-center rounded-full px-3 text-xs font-semibold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {rowQuantity > 0 ? `Scegli (${rowQuantity})` : "Scegli"}
              </button>
            ) : rowQuantity <= 0 ? (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onAdd(product); }}
                disabled={maxReached}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white shadow-sm disabled:opacity-40"
                style={{ backgroundColor: primaryColor }}
                aria-label={`Aggiungi ${product?.name || "prodotto"}`}
              >
                <Plus className="h-4 w-4" />
              </button>
            ) : (
              <div className="inline-flex h-8 shrink-0 items-center overflow-hidden rounded-full border border-[#dbe5ef] bg-white">
                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); onDecrease(product); }}
                  className="inline-flex h-8 w-8 items-center justify-center text-[#334155]"
                  aria-label={`Rimuovi ${product?.name || "prodotto"}`}
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[24px] text-center text-sm font-semibold text-[#0f172a]">{rowQuantity}</span>
                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); onAdd(product); }}
                  disabled={maxReached}
                  className="inline-flex h-8 w-8 items-center justify-center text-[#334155] disabled:opacity-40"
                  aria-label={`Aumenta ${product?.name || "prodotto"}`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// HomePage
// ---------------------------------------------------------------------------

export default function HomePage() {
  const { tenantId = "default" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useAppNavigate();
  const searchInputRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const [webappCustomer, setWebappCustomer] = useState(null); // null = unknown, false = guest
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(61);

  // --- branding + catalog --------------------------------------------------
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [webappLayout, setWebappLayout] = useState(DEFAULT_WEBAPP_DESIGN.layout);
  const [categories, setCategories] = useState([]);
  const [assortmentProducts, setAssortmentProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [frequentProducts, setFrequentProducts] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  // Filtro prodotti da CTA banner "prodotti promozione": { id, name }
  const [promoFilter, setPromoFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [assortmentPage, setAssortmentPage] = useState(1);
  const [assortmentTotal, setAssortmentTotal] = useState(0);
  const [assortmentHasMore, setAssortmentHasMore] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // --- cart ----------------------------------------------------------------
  // Lazy-initialise from localStorage so the cart survives a page refresh or back-navigation.
  const [cartLines, setCartLines] = useState(() => loadCart(tenantId));

  // --- main tab state (Frequenti/Assortimento) -----------------------------
  const [selectedTab, setSelectedTab] = useState("assortment");

  // --- drawer state --------------------------------------------------------
  // Only one is non-null/true at a time.
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailEditingLine, setDetailEditingLine] = useState(null);
  const [detailReturnProduct, setDetailReturnProduct] = useState(null);
  const [inCartProduct, setInCartProduct] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const [storeInfo, setStoreInfo] = useState({});
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [showRegDrawer, setShowRegDrawer] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState("wa_otp_custom"); // from config
  const [inventoryManagement, setInventoryManagement] = useState(false); // from config
  const [catalogAccess, setCatalogAccess] = useState("public"); // from config
  const profileMenuRef = useRef(null);

  // --- theme ---------------------------------------------------------------
  const primaryColor = useMemo(
    () => normalizeHexColor(branding.primaryColor, DEFAULT_BRANDING.primaryColor),
    [branding.primaryColor]
  );
  const accentColor = useMemo(
    () => normalizeHexColor(branding.accentColor, DEFAULT_BRANDING.accentColor),
    [branding.accentColor]
  );
  const tenantTheme = useMemo(
    () => ({ "--tenant-primary": primaryColor, "--tenant-accent": accentColor }),
    [accentColor, primaryColor]
  );
  const resolvedLogoUrl = branding.logoUrl || logoFallbackUrl;
  const isAuthenticatedCustomer = Boolean(webappCustomer && !webappCustomer.guest);
  const frequentCategoryEnabled = isAuthenticatedCustomer && (webappCustomer?.totalOrders > 0);
  const isFrequentTabActive = selectedTab === "frequent" && frequentCategoryEnabled;
  const profileInitials = useMemo(() => initialsFromCustomer(webappCustomer), [webappCustomer]);
  const whatsappContactUrl = useMemo(() => toWhatsAppLink(storeInfo?.phone), [storeInfo?.phone]);

  // --- effects -------------------------------------------------------------
  useEffect(() => {
    sessionStorage.setItem("webapp_tenant_id", tenantId);
  }, [tenantId]);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    saveCart(tenantId, cartLines);
  }, [tenantId, cartLines]);

  // Handle ?token=xxx from magic link URL: store in sessionStorage, clean URL
  useEffect(() => {
    const urlToken = searchParams.get("token");
    if (urlToken) {
      sessionStorage.setItem("access_token", urlToken);
      setSearchParams((prev) => { prev.delete("token"); return prev; }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load customer from API whenever access_token is available
  useEffect(() => {
    let active = true;
    async function loadCustomer() {
      const token = resolveToken(tenantId);
      if (!token) { setWebappCustomer(false); return; }
      try {
        const { data } = await api.get("/webapp/customers/me", { headers: { "x-tenant-id": tenantId } });
        if (!active) return;
        const payload = data?.data || {};
        setWebappCustomer(payload.guest ? false : (payload.customer || false));
      } catch {
        if (active) setWebappCustomer(false);
      }
    }
    loadCustomer();
    return () => { active = false; };
  }, [tenantId]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 180);
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    let active = true;
    async function loadConfig() {
      try {
        const { data } = await api.get("/webapp/config", { headers: { "x-tenant-id": tenantId } });
        if (!active) return;
        const payload = data?.data || {};
        const nextBranding = {
          appName: payload?.branding?.appName || DEFAULT_BRANDING.appName,
          logoUrl: payload?.branding?.logoUrl || "",
          primaryColor: normalizeHexColor(payload?.branding?.primaryColor, DEFAULT_BRANDING.primaryColor),
          accentColor: normalizeHexColor(payload?.branding?.accentColor, DEFAULT_BRANDING.accentColor),
          subtitle: payload?.branding?.subtitle || DEFAULT_BRANDING.subtitle,
          accentTitle: payload?.branding?.accentTitle || "",
        };
        const storeInfo = payload?.storeInfo || {};
        const description = [storeInfo.address, storeInfo.city, storeInfo.phone].filter(Boolean).join(" \u2022 ");
        setBranding(nextBranding);
        setWebappLayout(["standard", "grid", "promotional"].includes(payload?.webappDesign?.layout)
          ? payload.webappDesign.layout
          : DEFAULT_WEBAPP_DESIGN.layout);
        setStoreInfo(storeInfo);
        setCheckoutMode(payload?.checkoutMode || "wa_otp_custom");
        setInventoryManagement(payload?.inventoryManagement === true);
        setCatalogAccess(payload?.catalogAccess || "public");
        applyDocumentMetadata({
          title: nextBranding.appName,
          description: description || "Catalogo online del negozio con ordine rapido WhatsApp.",
          image: nextBranding.logoUrl || "",
          siteName: nextBranding.appName,
          url: window.location.href,
        });
      } catch {
        if (!active) return;
      }
    }
    loadConfig();
    return () => { active = false; };
  }, [tenantId]);

  useEffect(() => {
    let active = true;
    async function loadAssortment() {
      if (webappCustomer === null || selectedTab === "frequent") {
        return;
      }
      setLoading(true);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      setError("");
      try {
        const params = { page: 1, limit: 30, tab: "assortment" };
        if (selectedCategoryId !== "all") params.category = selectedCategoryId;
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;
        if (promoFilter?.id) params.promotion = promoFilter.id;
        const { data } = await api.get("/webapp/products", { params, headers: { "x-tenant-id": tenantId } });
        if (!active) return;
        const items = Array.isArray(data?.data) ? data.data : [];
        const meta = data?.meta || {};
        const total = Number.isFinite(Number(meta.total)) ? Number(meta.total) : items.length;
        setAssortmentProducts(items);
        setAssortmentPage(1);
        setAssortmentTotal(total);
        setAssortmentHasMore(items.length < total);
        setCategories(Array.isArray(meta.categories) ? meta.categories : []);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.error?.message || "Impossibile caricare i prodotti.");
        setAssortmentProducts([]);
        setAssortmentPage(1);
        setAssortmentTotal(0);
        setAssortmentHasMore(false);
        setCategories([]);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadAssortment();
    return () => { active = false; };
  }, [selectedCategoryId, tenantId, debouncedSearchTerm, selectedTab, webappCustomer, promoFilter]);

  useEffect(() => {
    if (selectedTab === "frequent" || loading || !assortmentHasMore || error) return undefined;
    const target = loadMoreRef.current;
    if (!target) return undefined;

    let active = true;
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry?.isIntersecting || loadingMoreRef.current || !active) return;

        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
          const nextPage = assortmentPage + 1;
          const params = { page: nextPage, limit: 30, tab: "assortment" };
          if (selectedCategoryId !== "all") params.category = selectedCategoryId;
          if (debouncedSearchTerm) params.search = debouncedSearchTerm;
          if (promoFilter?.id) params.promotion = promoFilter.id;

          const { data } = await api.get("/webapp/products", {
            params,
            headers: { "x-tenant-id": tenantId },
          });
          if (!active) return;

          const items = Array.isArray(data?.data) ? data.data : [];
          const meta = data?.meta || {};
          const total = Number.isFinite(Number(meta.total)) ? Number(meta.total) : assortmentTotal;

          setAssortmentProducts((current) => {
            const existingIds = new Set(current.map((product) => String(product?._id || "")));
            const nextItems = items.filter((product) => !existingIds.has(String(product?._id || "")));
            const nextProducts = [...current, ...nextItems];
            setAssortmentHasMore(items.length > 0 && nextProducts.length < total);
            return nextProducts;
          });
          setAssortmentPage(nextPage);
          setAssortmentTotal(total);
        } catch (err) {
          if (active) setError(err?.response?.data?.error?.message || "Impossibile caricare altri prodotti.");
        } finally {
          if (active) setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      },
      { root: null, rootMargin: "600px 0px", threshold: 0 }
    );

    observer.observe(target);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [assortmentHasMore, assortmentPage, assortmentTotal, debouncedSearchTerm, error, loading, selectedCategoryId, selectedTab, tenantId, promoFilter]);

  useEffect(() => {
    if (webappCustomer === null || webappLayout !== "promotional" || selectedTab === "frequent") {
      setFeaturedProducts([]);
      return undefined;
    }

    let active = true;
    async function loadFeaturedProducts() {
      try {
        const params = { page: 1, limit: 10, tab: "assortment", featured: true };
        if (selectedCategoryId !== "all") params.category = selectedCategoryId;
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;

        const { data } = await api.get("/webapp/products", {
          params,
          headers: { "x-tenant-id": tenantId },
        });
        if (!active) return;
        setFeaturedProducts(Array.isArray(data?.data) ? data.data : []);
      } catch {
        if (!active) return;
        setFeaturedProducts([]);
      }
    }

    loadFeaturedProducts();
    return () => { active = false; };
  }, [debouncedSearchTerm, selectedCategoryId, selectedTab, tenantId, webappCustomer, webappLayout]);

  useEffect(() => {
    if (webappCustomer === null || selectedTab !== "frequent") return;
    let active = true;
    setLoading(true);
    setError("");
    async function loadFrequent() {
      try {
        const { data } = await api.get("/webapp/products/frequent", {
          params: { page: 1, limit: 30 },
          headers: { "x-tenant-id": tenantId },
        });
        if (!active) return;
        setFrequentProducts(Array.isArray(data?.data) ? data.data : []);
      } catch {
        if (!active) return;
        setFrequentProducts([]);
        setError("Impossibile caricare i prodotti frequenti.");
      } finally {
        if (active) setLoading(false);
      }
    }
    loadFrequent();
    return () => { active = false; };
  }, [selectedTab, tenantId, webappCustomer]);

  useEffect(() => {
    if (!frequentCategoryEnabled && selectedTab === "frequent") {
      setSelectedTab("assortment");
    }
  }, [frequentCategoryEnabled, selectedTab]);

  useEffect(() => {
    let active = true;
    async function loadBanners() {
      try {
        const { data } = await api.get("/webapp/banners", { headers: { "x-tenant-id": tenantId } });
        if (!active) return;
        setPromoBanners(Array.isArray(data?.data) ? data.data : []);
      } catch {
        // banners are optional — ignore errors
      }
    }
    loadBanners();
    return () => { active = false; };
  }, [tenantId]);

  useEffect(() => {
    let active = true;

    async function loadPendingOrders() {
      if (!isAuthenticatedCustomer) {
        setPendingOrdersCount(0);
        return;
      }

      try {
        const { data } = await api.get("/webapp/orders", {
          params: { page: 1, limit: 1 },
          headers: { "x-tenant-id": tenantId },
        });

        if (!active) return;
        setPendingOrdersCount(Number(data?.meta?.pendingCount) || 0);
      } catch {
        if (!active) return;
        setPendingOrdersCount(0);
      }
    }

    loadPendingOrders();
    return () => {
      active = false;
    };
  }, [isAuthenticatedCustomer, tenantId]);

  useEffect(() => {
    if (!showProfileMenu) return undefined;

    function handleClickOutside(event) {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }

    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileMenu]);

  useEffect(() => {
    function syncStickyHeaderHeight() {
      if (!stickyHeaderRef.current) {
        return;
      }
      const nextHeight = Math.ceil(stickyHeaderRef.current.getBoundingClientRect().height);
      setStickyHeaderHeight((current) => (current === nextHeight ? current : nextHeight));
    }

    syncStickyHeaderHeight();
    window.addEventListener("resize", syncStickyHeaderHeight);

    let observer;
    if (typeof ResizeObserver !== "undefined" && stickyHeaderRef.current) {
      observer = new ResizeObserver(syncStickyHeaderHeight);
      observer.observe(stickyHeaderRef.current);
    }

    return () => {
      window.removeEventListener("resize", syncStickyHeaderHeight);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // --- derived catalog -----------------------------------------------------
  const baseProducts = selectedTab === "frequent" ? frequentProducts : assortmentProducts;
  const visibleProducts = useMemo(() => {
    if (selectedTab !== "frequent" || !debouncedSearchTerm) return baseProducts;
    const q = debouncedSearchTerm.toLowerCase();
    return baseProducts.filter((p) =>
      String(p?.name || "").toLowerCase().includes(q) ||
      String(p?.description || "").toLowerCase().includes(q)
    );
  }, [selectedTab, baseProducts, debouncedSearchTerm]);

  const categoryChips = useMemo(() => {
    const chips = [{ _id: "all", name: "Tutti", icon: "" }];
    if (frequentCategoryEnabled) {
      chips.push({ _id: "__frequent__", name: "Frequenti", icon: "heart" });
    }
    chips.push(...categories);
    return chips;
  }, [categories, frequentCategoryEnabled]);

  const topBanners = useMemo(() => promoBanners.filter((b) => b.showAtTop), [promoBanners]);
  const inlineBanners = useMemo(() => promoBanners.filter((b) => b.insertEvery > 0), [promoBanners]);

  const productListItems = useMemo(() => {
    if (!inlineBanners.length) return visibleProducts.map((p) => ({ kind: "product", product: p }));
    const step = inlineBanners[0].insertEvery;
    if (!step || step <= 0) return visibleProducts.map((p) => ({ kind: "product", product: p }));
    const result = [];
    for (let i = 0; i < visibleProducts.length; i++) {
      result.push({ kind: "product", product: visibleProducts[i] });
      if ((i + 1) % step === 0 && i < visibleProducts.length - 1) {
        const bannerIdx = (Math.floor((i + 1) / step) - 1) % inlineBanners.length;
        result.push({ kind: "banner", banner: inlineBanners[bannerIdx] });
      }
    }
    return result;
  }, [visibleProducts, inlineBanners]);

  const topPromotionalProducts = useMemo(() => {
    if (webappLayout !== "promotional" || selectedTab === "frequent") return [];
    return featuredProducts.slice(0, 10);
  }, [featuredProducts, selectedTab, webappLayout]);

  const cardLayoutEnabled = webappLayout === "grid";

  // --- derived cart --------------------------------------------------------
  const productQuantityMap = useMemo(() => {
    return cartLines.reduce((acc, line) => {
      const key = String(line.productId || "");
      acc[key] = (acc[key] || 0) + (Number(line.quantity) || 0);
      return acc;
    }, {});
  }, [cartLines]);

  const cartSummary = useMemo(() => {
    return cartLines.reduce(
      (acc, line) => {
        acc.items += Number(line.quantity) || 0;
        acc.total += (Number(line.unitPrice) || 0) * (Number(line.quantity) || 0);
        return acc;
      },
      { items: 0, total: 0 }
    );
  }, [cartLines]);

  // --- drawer derived ------------------------------------------------------
  const detailProductQtyInCart = useMemo(() => {
    if (!detailProduct?._id) return 0;
    return productQuantityMap[String(detailProduct._id)] || 0;
  }, [detailProduct, productQuantityMap]);

  const inCartProductLines = useMemo(() => {
    if (!inCartProduct?._id) return [];
    return cartLines.filter((l) => String(l.productId) === String(inCartProduct._id));
  }, [inCartProduct, cartLines]);

  const detailHasImage = useMemo(() => {
    return Boolean(detailProduct?.images?.some((img) => String(img?.url || "").trim()));
  }, [detailProduct]);

  // --- drawer navigation ---------------------------------------------------

  function openDetail(product, editingLine, returnProduct) {
    setInCartProduct(null);
    setIsCartOpen(false);
    setDetailProduct(product);
    setDetailEditingLine(editingLine || null);
    setDetailReturnProduct(returnProduct || null);
  }

  function openInCart(product) {
    const lines = cartLines.filter((l) => String(l.productId) === String(product?._id || ""));
    if (!lines.length) {
      openDetail(product, null, null);
      return;
    }
    setDetailProduct(null);
    setDetailEditingLine(null);
    setDetailReturnProduct(null);
    setIsCartOpen(false);
    setInCartProduct(product);
  }

  function openCart() {
    setDetailProduct(null);
    setDetailEditingLine(null);
    setDetailReturnProduct(null);
    setInCartProduct(null);
    setIsCartOpen(true);
  }

  // Unified: row tap or action button
  function handleOpenProduct(product) {
    if (isConfigurableProduct(product)) {
      openInCart(product); // falls back to openDetail if not in cart
    } else {
      openDetail(product, null, null);
    }
  }

  // Row "+" for simple, "Scegli" for configurable
  function handleRowAdd(product) {
    if (isConfigurableProduct(product)) {
      openInCart(product);
    } else {
      addSimpleProduct(product);
    }
  }

  // --- cart mutations -------------------------------------------------------

  function addSimpleProduct(product) {
    const productId = String(product?._id || "");
    if (!productId) return;
    const maxQty = resolveMaxQuantity(product, inventoryManagement);
    const alreadyInCart = productQuantityMap[productId] || 0;
    if (maxQty === 0) return;
    if (Number.isFinite(maxQty) && alreadyInCart >= maxQty) return;

    const lineId = buildCartLineId(productId, "", {}, null);
    const unitPrice = resolveProductStartingPrice(product);
    setCartLines((current) => {
      const existing = current.find((l) => l.id === lineId);
      if (existing) {
        return current.map((l) => l.id === lineId ? { ...l, quantity: l.quantity + 1 } : l);
      }
      return [
        ...current,
        {
          id: lineId,
          productId,
          productName: product?.name || "Prodotto",
          imageUrl: getProductImage(product),
          quantity: 1,
          unitPrice,
          variantId: "",
          variantLabel: "",
          optionValues: {},
          optionSignature: "",
          optionSummaries: [],
          variableSelection: null,
          isConfigurable: false,
          productSnapshot: product,
        },
      ];
    });
  }

  function decreaseProduct(product) {
    const productId = String(product?._id || "");
    if (!productId) return;
    const lineId = buildCartLineId(productId, "", {}, null);
    const line = cartLines.find((l) => l.id === lineId);
    if (!line) return;
    updateCartLineQuantity(lineId, line.quantity - 1);
  }

  function setSimpleProductQty(product, qty) {
    const productId = String(product?._id || "");
    if (!productId) return;
    const lineId = buildCartLineId(productId, "", {}, null);
    const maxQty = resolveMaxQuantity(product, inventoryManagement);
    const clampedQty = Number.isFinite(maxQty) ? Math.min(qty, maxQty) : qty;
    if (clampedQty <= 0) {
      setCartLines((current) => current.filter((l) => l.id !== lineId));
      return;
    }
    const unitPrice = resolveProductStartingPrice(product);
    setCartLines((current) => {
      const existing = current.find((l) => l.id === lineId);
      if (existing) {
        return current.map((l) => l.id === lineId ? { ...l, quantity: clampedQty } : l);
      }
      return [
        ...current,
        {
          id: lineId,
          productId,
          productName: product?.name || "Prodotto",
          imageUrl: getProductImage(product),
          quantity: clampedQty,
          unitPrice,
          variantId: "",
          variantLabel: "",
          optionValues: {},
          optionSignature: "",
          optionSummaries: [],
          variableSelection: null,
          isConfigurable: false,
          productSnapshot: product,
        },
      ];
    });
  }

  function updateCartLineQuantity(lineId, nextQty) {
    const qty = Math.max(0, Number(nextQty) || 0);
    setCartLines((current) =>
      current
        .map((l) => (l.id === lineId ? { ...l, quantity: qty } : l))
        .filter((l) => l.quantity > 0)
    );
  }

  function changeCartLineQuantity(line, delta) {
    if (!line) return;
    const next = Math.max(0, (Number(line.quantity) || 0) + delta);
    const max = resolveMaxQuantity(line.productSnapshot, inventoryManagement);
    const currentTotal = productQuantityMap[String(line.productId)] || 0;
    const others = Math.max(0, currentTotal - (Number(line.quantity) || 0));
    if (Number.isFinite(max) && next > 0 && others + next > max) return;
    updateCartLineQuantity(line.id, next);
  }

  // Called by DrawerProductDetail on "AGGIUNGI ALL'ORDINE" / "SALVA MODIFICHE"
  function handleSaveConfigure(variantId, optionValues, quantity, editingLineId, variableSelection, itemNotes) {
    if (!detailProduct) return;

    const productId = String(detailProduct._id || "");
    const normalized = cloneOptionValues(optionValues);
    const signature = serializeOptionValues(normalized);
    const normalizedVariableSelection = variableSelection && Number(variableSelection.value) > 0
      ? {
          value: Number(variableSelection.value),
          unit: String(variableSelection.unit || "").trim(),
        }
      : null;
    const lineId = buildCartLineId(productId, variantId, normalized, normalizedVariableSelection);
    const unitPrice = resolveUnitPriceForSelection(detailProduct, variantId, normalized, normalizedVariableSelection);
    const variantLabel =
      detailProduct?.variants?.find((v) => String(v._id) === variantId)?.name || "";
    const productOptions = normalizeProductOptions(detailProduct?.options);
    const optionSummaries = buildOptionSummaries(productOptions, normalized);
    if (normalizedVariableSelection) {
      optionSummaries.unshift({
        label: "Taglio",
        value: `${normalizedVariableSelection.value} ${normalizedVariableSelection.unit || ""}`.trim(),
        _isVariableLabel: true,
      });
    }
    const isEditing = Boolean(editingLineId);

    setCartLines((current) => {
      const baseLine = {
        id: lineId,
        productId,
        productName: detailProduct?.name || "Prodotto",
        imageUrl: getProductImage(detailProduct),
        quantity: Math.max(1, Number(quantity) || 1),
        unitPrice,
        variantId: String(variantId || ""),
        variantLabel,
        optionValues: normalized,
        optionSignature: signature,
        optionSummaries,
        variableSelection: normalizedVariableSelection,
        notes: itemNotes || "",
        isConfigurable: true,
        productSnapshot: detailProduct,
      };

      if (isEditing) {
        const withoutEdited = current.filter((l) => l.id !== editingLineId);
        const mergeTarget = withoutEdited.find((l) => l.id === lineId);
        if (mergeTarget) {
          return withoutEdited.map((l) =>
            l.id === lineId ? { ...l, quantity: l.quantity + baseLine.quantity } : l
          );
        }
        return [...withoutEdited, baseLine];
      }

      const existing = current.find((l) => l.id === lineId);
      if (existing) {
        return current.map((l) =>
          l.id === lineId ? { ...l, quantity: l.quantity + baseLine.quantity } : l
        );
      }
      return [...current, baseLine];
    });

    // After save: return to inCart if we came from there, otherwise close
    const returnProduct = detailReturnProduct;
    setDetailProduct(null);
    setDetailEditingLine(null);
    setDetailReturnProduct(null);
    if (returnProduct) setInCartProduct(returnProduct);
  }

  // DrawerInCart -> "Aggiungi"
  function handleAddNewFromInCart() {
    const product = inCartProduct;
    if (!product) return;
    openDetail(product, null, product); // return to inCart after save
  }

  // DrawerInCart -> "Modifica"
  function handleEditLineFromInCart(line) {
    const product = inCartProduct;
    if (!product || !line) return;
    openDetail(product, line, product); // return to inCart after save
  }

  // DrawerCart -> "Modifica"
  function handleEditLineFromCart(line) {
    const product = line?.productSnapshot;
    if (!product) return;
    setIsCartOpen(false);
    openDetail(product, line, null); // just close after save
  }

  // CTA banner "prodotti promozione"
  function handleShowPromotion(promo) {
    if (!promo?._id) return;
    setSelectedTab("assortment");
    setSelectedCategoryId("all");
    setSearchTerm("");
    setPromoFilter({ id: String(promo._id), name: promo.name || "Promozione" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // --- style helpers -------------------------------------------------------
  const selectedChipStyle = { borderColor: primaryColor, backgroundColor: primaryColor, color: "#ffffff" };
  const selectedRowStyle = { borderColor: primaryColor, backgroundColor: hexToRgba(primaryColor, 0.08) };
  const emptyCart = cartSummary.items <= 0;
  const footerPanelStyle = { borderColor: hexToRgba(primaryColor, 0.18) };
  const footerSearchStyle = {
    borderColor: hexToRgba(primaryColor, 0.3),
    boxShadow: `0 0 0 1px ${hexToRgba(primaryColor, 0.08)} inset`,
  };
  const footerCartButtonStyle = {
    backgroundColor: primaryColor,
    boxShadow: `0 8px 18px ${hexToRgba(primaryColor, 0.28)}`,
  };

  const anyDrawerOpen = Boolean(detailProduct) || Boolean(inCartProduct) || isCartOpen;

  // --- catalog gated screen (catalogAccess === "authenticated") -------------
  // webappCustomer === null means we're still loading; false means unauthenticated.
  if (catalogAccess === "authenticated" && webappCustomer === false) {
    return (
      <div className="min-h-screen bg-[#efefef]" style={tenantTheme}>
        <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col items-center justify-center px-6 bg-white shadow-sm">
          <img src={resolvedLogoUrl} alt={branding.appName} className="mb-8 h-14 w-auto object-contain" />
          <div
            className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: hexToRgba(accentColor, 0.12) }}
          >
            <User className="h-8 w-8" style={{ color: accentColor }} />
          </div>
          <h1 className="text-xl font-bold text-[#0f172a]">Accesso richiesto</h1>
          <p className="mt-2 text-center text-sm text-[#64748b]">
            Questo catalogo è riservato agli utenti registrati. Accedi per visualizzare i prodotti.
          </p>
          <button
            type="button"
            onClick={() => setShowRegDrawer(true)}
            className="mt-8 flex h-12 w-full items-center justify-center rounded-xl text-base font-semibold text-white shadow-sm"
            style={{ backgroundColor: accentColor }}
          >
            Accedi
          </button>
          <AuthDrawer
            tenantId={tenantId}
            open={showRegDrawer}
            onClose={() => setShowRegDrawer(false)}
            onAuthenticated={(customer, accessToken) => {
              if (accessToken) savePersistentToken(tenantId, accessToken);
              setWebappCustomer(customer);
              setShowRegDrawer(false);
            }}
            primaryColor={primaryColor}
            accentColor={accentColor}
            logoUrl={branding.logoUrl}
            appName={branding.appName}
            checkoutMode={checkoutMode}
          />
        </div>
      </div>
    );
  }

  // --- render --------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#efefef]" style={tenantTheme}>
      <div className="mx-auto min-h-screen w-full max-w-[800px] bg-white shadow-sm">

        {/* Sticky header */}
        <div ref={stickyHeaderRef} className="sticky top-0 z-30 border-b border-[#e2e8f0] bg-white">
          <header className="px-4 pb-5 pt-5">
            <div className="flex items-center justify-between gap-2">
              <img src={resolvedLogoUrl} alt={branding.appName} className="h-9 w-[120px] object-contain object-left" />
              <div className="relative flex items-center gap-2" ref={profileMenuRef}>
                {isAuthenticatedCustomer ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/${tenantId}/orders`)}
                    className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#64748b]"
                    aria-label="Storico ordini"
                  >
                    <ClipboardList className="h-4 w-4" />
                    {pendingOrdersCount > 0 ? (
                      <span
                        className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {pendingOrdersCount}
                      </span>
                    ) : null}
                  </button>
                ) : null}

                {!isAuthenticatedCustomer && checkoutMode !== "wa_redirect" ? (
                  <button
                    type="button"
                    onClick={() => setShowRegDrawer(true)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#64748b]"
                    aria-label="Accedi"
                  >
                    <User className="h-4 w-4" />
                  </button>
                ) : null}
                {isAuthenticatedCustomer ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowProfileMenu((current) => !current)}
                      className="relative inline-flex h-10 min-w-10 items-center justify-center rounded-full border border-[#d7e0ea] bg-[#0f172a] px-3 text-[11px] font-semibold text-white"
                      aria-label="Apri menu profilo"
                    >
                      {profileInitials}
                      {((webappCustomer?.credit?.balance ?? 0) > 0 || (webappCustomer?.welfareCredit?.balance ?? 0) > 0) ? (
                        <span
                          className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[9px] font-bold text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          €
                        </span>
                      ) : null}
                    </button>

                    {showProfileMenu ? (
                      <div className="absolute right-0 top-[46px] z-40 w-56 overflow-hidden rounded-xl border border-[#dbe5ef] bg-white shadow-lg">
                        {/* Credit summary */}
                        {((webappCustomer?.credit?.balance ?? 0) > 0 || (webappCustomer?.welfareCredit?.balance ?? 0) > 0) ? (
                          <div className="border-b border-[#e2e8f0] px-3 py-2.5">
                            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#94a3b8]">Il mio credito</p>
                            {(webappCustomer?.credit?.balance ?? 0) > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#475569]">Credito negozio</span>
                                <span className="font-semibold text-[#0f172a]">€{Number(webappCustomer.credit.balance).toFixed(2)}</span>
                              </div>
                            )}
                            {(webappCustomer?.welfareCredit?.balance ?? 0) > 0 && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-[#475569]">Credito welfare</span>
                                <span className="font-semibold text-[#0f172a]">€{Number(webappCustomer.welfareCredit.balance).toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate(`/${tenantId}/profile`);
                          }}
                          className="flex h-10 w-full items-center justify-between px-3 text-sm text-[#0f172a] hover:bg-[#f8fafc]"
                        >
                          <span>Il mio profilo</span>
                          <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-[#94a3b8]" />
                        </button>
                        {whatsappContactUrl ? (
                          <a
                            href={whatsappContactUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => setShowProfileMenu(false)}
                            className="flex h-10 items-center justify-between px-3 text-sm text-[#0f172a] hover:bg-[#f8fafc]"
                          >
                            <span>Contattaci</span>
                            <MessageCircle className="h-3.5 w-3.5 text-[#94a3b8]" />
                          </a>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
          </header>

        </div>

        {/* Product list */}
        <main className="pb-[126px]">

          {/* Greeting (hidden when top banners are present) */}
          {topBanners.length === 0 && ((webappCustomer && !webappCustomer.guest) || (branding.subtitle || branding.accentTitle))&&  (
            <div className="px-4 pb-2 pt-3">
              {webappCustomer && !webappCustomer.guest ? (
                <>
                  <p className="text-[20px] font-bold leading-tight" style={{ color: accentColor }}>
                    Bentornata {webappCustomer.name},
                  </p>
                  <p className="text-[20px] font-bold leading-tight text-[#0f172a]">
                    facciamo un nuovo ordine?
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[20px] font-bold leading-tight text-[#0f172a]">
                    {branding.subtitle}
                  </p>
                  {branding.accentTitle && (
                    <p className="mt-0.5 text-[20px] font-bold leading-tight" style={{ color: accentColor }}>
                      {branding.accentTitle}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {topBanners.length > 0 && (
            <BannerCarousel banners={topBanners} primaryColor={primaryColor} tenantId={tenantId} navigate={navigate} onShowPromotion={handleShowPromotion} />
          )}

          {/* Category chips — always right before product list */}
          <div
            className="sticky z-20 -mt-px border-b border-[#eef2f7] bg-white px-4 pb-3 pt-3"
            style={{ top: `${stickyHeaderHeight}px` }}
          >
            <div className="no-scrollbar flex gap-2 overflow-x-auto">
              {categoryChips.map((category) => {
                const isFrequentChip = String(category._id) === "__frequent__";
                const active = isFrequentChip
                  ? isFrequentTabActive
                  : (selectedTab !== "frequent" && String(selectedCategoryId) === String(category._id));
                return (
                  <button
                    key={String(category._id)}
                    type="button"
                    onClick={() => {
                      if (isFrequentChip) {
                        setSelectedTab("frequent");
                        setSelectedCategoryId("all");
                        return;
                      }
                      setSelectedTab("assortment");
                      setSelectedCategoryId(String(category._id));
                    }}
                    className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-[#cbd5e1] bg-white px-3 text-sm font-semibold text-[#020617]"
                    style={active ? selectedChipStyle : null}
                  >
                    <CategoryChipIcon icon={category.icon}/>
                    <span>{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {webappLayout === "promotional" && selectedTab !== "frequent" && topPromotionalProducts.length > 0 ? (
            <section className="border-b border-[#eef2f7] py-3">
              <div className="mb-2 flex items-center justify-between px-4">
                <h2 className="text-sm font-bold text-[#0f172a]">In evidenza</h2>
                <span className="text-[11px] text-[#64748b]">I prodotti più scontati</span>
              </div>
              <div className="no-scrollbar flex gap-3 overflow-x-auto px-4 pb-1">
                {topPromotionalProducts.map((product) => (
                  <ProductCard
                    key={`top-${String(product?._id || "")}`}
                    product={product}
                    primaryColor={primaryColor}
                    inventoryManagement={inventoryManagement}
                    productQuantityMap={productQuantityMap}
                    onOpen={handleOpenProduct}
                    onAdd={handleRowAdd}
                    onDecrease={decreaseProduct}
                    featured
                  />
                ))}
              </div>
            </section>
          ) : null}

          <div className="flex items-center justify-between px-4 py-2">
            <h2 className="text-md font-semibold text-[#0f172a]">
              {selectedTab === "frequent"
                ? "Tutti i frequenti"
                : promoFilter
                  ? promoFilter.name
                  : "Tutto l'assortimento"}
            </h2>
            <div className="inline-flex items-center gap-2">
              {promoFilter && selectedTab !== "frequent" ? (
                <button
                  type="button"
                  onClick={() => setPromoFilter(null)}
                  className="inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-[11px] font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  <span>In promo</span>
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
              <div className="inline-flex items-center gap-1 text-[11px] text-[#64748b]">
                <Search className="h-3.5 w-3.5" />
                <span>{visibleProducts.length} prodotti</span>
              </div>
            </div>
          </div>

          {loading && <div className="px-4 py-4 text-sm text-[#64748b]">Caricamento prodotti...</div>}
          {error && <div className="px-4 py-4 text-sm text-red-500">{error}</div>}

          {!loading && !error && !visibleProducts.length && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-semibold text-[#0f172a]">
                Nessun prodotto disponibile
              </p>
              <p className="mt-1 text-xs text-[#64748b]">
                Prova con un'altra categoria o cancella la ricerca.
              </p>
            </div>
          )}

          {!cardLayoutEnabled ? (
          <div>
            {productListItems.map((item, listIdx) => {
              if (item.kind === "banner") {
                return (
                  <div key={`banner-${item.banner._id}-${listIdx}`} className="px-4 py-2">
                    <div className="overflow-hidden rounded-xl">
                      <BannerCard banner={item.banner} primaryColor={primaryColor} tenantId={tenantId} navigate={navigate} onShowPromotion={handleShowPromotion} />
                    </div>
                  </div>
                );
              }
              const product = item.product;
              const productId = String(product?._id || "");
              const imageUrl = getProductImage(product);
              const hasImage = Boolean(String(imageUrl || "").trim());
              const rowQuantity = productQuantityMap[productId] || 0;
              const configurable = isConfigurableProduct(product);
              const maxQty = resolveMaxQuantity(product, inventoryManagement);
              const soldOut = maxQty === 0;
              const maxReached = Number.isFinite(maxQty) && rowQuantity >= maxQty;
              const { label: unitLabel, hasMultiple: unitHasMultiple } = resolveProductUnitInfo(product);
              const priceInfo = resolveProductPriceInfo(product);
              const hasVisiblePrice = Number(priceInfo.current) > 0;
              const currentPriceLabel = hasVisiblePrice
                ? (product?.hasVariants ? `da ${formatCurrency(priceInfo.current)}` : formatCurrency(priceInfo.current))
                : null;

              return (
                <article
                  key={productId}
                  className="cursor-pointer border-b border-[#dfe6ee] px-4 py-2.5 transition-colors hover:bg-[#f8fafc]"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenProduct(product)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOpenProduct(product); }
                  }}
                >
                  <div className="flex min-h-[84px] items-center gap-2.5">
                    {hasImage ? (
                      <div
                        className="shrink-0 overflow-hidden rounded-xl bg-[#f1f5f9]"
                        style={{ width: 76, height: 76, minWidth: 76, maxWidth: 76, flex: "0 0 76px" }}
                      >
                        <img
                          src={imageUrl}
                          alt={product?.name || "Prodotto"}
                          style={{ width: 76, height: 76, display: "block", objectFit: "cover" }}
                        />
                      </div>
                    ) : null}

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold leading-5 text-[#0f172a]">{product?.name || "Prodotto"}
                        {priceInfo.hasDiscount ? (
                          <span className="rounded-full bg-emerald-50 px-2 ms-2 text-[10px] font-semibold text-emerald-700">Promo</span>
                        ) : null}
                      </p>
                      {product?.description && <p className="mt-0.5 line-clamp-2 text-sm leading-4 text-[#64748b]">{product?.description || "\u2014"}</p>}
                      <div className="mt-1.5 flex items-center gap-2 text-sm text-[#64748b]">
                        {unitHasMultiple ? (
                          <span className="font-medium text-[#475569]">Vedi opzioni</span>
                        ) : unitLabel ? (
                          <span>{unitLabel}</span>
                        ) : null}
                        {(unitHasMultiple || unitLabel) && hasVisiblePrice ? <span>|</span> : null}
                        {hasVisiblePrice ? (
                          <span className="text-sm font-semibold text-[#020617]">{currentPriceLabel}</span>
                        ) : null}
                        {soldOut && (
                          <span className="rounded-full bg-[#f8d7da] px-2 py-0.5 text-[10px] font-semibold text-[#b42318]">Esaurito</span>
                        )}
                      </div>
                    </div>

                    <div className="ml-auto flex shrink-0 items-center justify-end">
                      {soldOut ? (
                        <div className="inline-flex h-8 items-center rounded-full border border-[#e2e8f0] px-3 text-xs text-[#64748b]">0</div>
                      ) : configurable ? (
                        /* Configurable — always "Scegli" */
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRowAdd(product); }}
                          className="inline-flex h-8 items-center gap-1.5 rounded-full border border-[#dbe5ef] bg-white px-3 text-xs font-semibold text-[#0f172a]"
                          aria-label={`Scegli ${product?.name || "prodotto"}`}
                        >
                          <span>Scegli</span>
                          {rowQuantity > 0 && (
                            <span
                              className="inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {rowQuantity}
                            </span>
                          )}
                        </button>
                      ) : rowQuantity <= 0 ? (
                        /* Simple — not in cart */
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRowAdd(product); }}
                          disabled={maxReached}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white shadow-sm disabled:opacity-40"
                          style={{ backgroundColor: primaryColor }}
                          aria-label={`Aggiungi ${product?.name || "prodotto"}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      ) : (
                        /* Simple — inline stepper */
                        <div
                          className="inline-flex h-8 items-center overflow-hidden rounded-full border border-[#dbe5ef] bg-white"
                          style={selectedRowStyle}
                        >
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); decreaseProduct(product); }}
                            className="inline-flex h-8 w-8 items-center justify-center text-[#334155]"
                            aria-label={`Rimuovi ${product?.name || "prodotto"}`}
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="min-w-[24px] text-center text-sm font-semibold text-[#0f172a]">{rowQuantity}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRowAdd(product); }}
                            disabled={maxReached}
                            className="inline-flex h-8 w-8 items-center justify-center text-[#334155] disabled:opacity-40"
                            aria-label={`Aumenta ${product?.name || "prodotto"}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 px-4 py-2">
              {productListItems.map((item, listIdx) => {
                if (item.kind === "banner") {
                  return (
                    <div key={`banner-${item.banner._id}-${listIdx}`} className="col-span-2">
                      <div className="overflow-hidden rounded-xl">
                        <BannerCard banner={item.banner} primaryColor={primaryColor} tenantId={tenantId} navigate={navigate} onShowPromotion={handleShowPromotion} />
                      </div>
                    </div>
                  );
                }

                return (
                  <ProductCard
                    key={String(item.product?._id || listIdx)}
                    product={item.product}
                    primaryColor={primaryColor}
                    inventoryManagement={inventoryManagement}
                    productQuantityMap={productQuantityMap}
                    onOpen={handleOpenProduct}
                    onAdd={handleRowAdd}
                    onDecrease={decreaseProduct}
                  />
                );
              })}
            </div>
          )}

          {selectedTab !== "frequent" && (assortmentHasMore || loadingMore) ? (
            <div ref={loadMoreRef} className="px-4 py-5 text-center text-xs text-[#64748b]">
              {loadingMore ? "Caricamento altri prodotti..." : "Scorri per vedere altri prodotti"}
            </div>
          ) : null}
        </main>

        {/* Sticky footer — search + cart button */}
        {!anyDrawerOpen && (
          <div className="pointer-events-none fixed bottom-0 left-1/2 z-30 w-full max-w-[800px] -translate-x-1/2">
            <div
              className="pointer-events-auto rounded-t-3xl border border-b-0 bg-white px-3 pb-3 pt-2 shadow-[0_-3px_12px_rgba(0,0,0,0.08)]"
              style={footerPanelStyle}
            >
              <div className="flex items-center gap-2 px-2 py-1">
                <div
                  className="transition-all duration-300 ease-out"
                  style={{ width: emptyCart ? "100%" : "calc(100% - 150px)" }}
                >
                  <div className="flex h-10 items-center gap-2 rounded-xl bg-white px-3" style={footerSearchStyle}>
                    <Search className="h-4 w-4 text-[#64748b]" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Cerca prodotti..."
                      className="h-8 flex-1 bg-transparent text-sm text-[#0f172a] outline-none"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[#64748b]"
                        aria-label="Pulisci ricerca"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openCart}
                  className="inline-flex h-10 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-xl px-3 text-sm font-semibold text-white transition-all duration-300 ease-out"
                  style={{
                    ...footerCartButtonStyle,
                    width: emptyCart ? "0px" : "140px",
                    minWidth: emptyCart ? "0px" : "140px",
                    opacity: emptyCart ? 0 : 1,
                    pointerEvents: emptyCart ? "none" : "auto",
                  }}
                  aria-label="Apri carrello"
                >
                  <span className="relative inline-flex">
                    <ShoppingCart className="h-4 w-4 shrink-0" />
                    {cartSummary.items > 0 && (
                      <span className="absolute -right-2 -top-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[9px] font-bold text-[#0f172a]">
                        {cartSummary.items}
                      </span>
                    )}
                  </span>
                  <span className="truncate">{formatCurrency(cartSummary.total)}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DrawerProductDetail */}
        <Drawer
          open={Boolean(detailProduct)}
          onOpenChange={(open) => {
            if (!open && document.body.dataset.lightboxOpen === "true") {
              // Lightbox is open: close it instead of closing the drawer
              document.dispatchEvent(new CustomEvent("lightbox:close"));
              return;
            }
            if (!open) {
              setDetailProduct(null);
              setDetailEditingLine(null);
              setDetailReturnProduct(null);
            }
          }}
        >
          <DrawerContent
            showHandle={!detailHasImage}
            className="mx-auto w-full max-w-[800px] rounded-t-2xl border-[#e2e8f0] bg-white"
          >
            {detailProduct && (
              <DrawerProductDetail
                product={detailProduct}
                primaryColor={primaryColor}
                accentColor={accentColor}
                editingLine={detailEditingLine}
                productQtyInCart={detailProductQtyInCart}
                inventoryManagement={inventoryManagement}
                onClose={() => {
                  setDetailProduct(null);
                  setDetailEditingLine(null);
                  setDetailReturnProduct(null);
                }}
                onSaveConfigure={handleSaveConfigure}
                onAddSimple={addSimpleProduct}
                onDecreaseSimple={decreaseProduct}
                onSetSimpleQty={setSimpleProductQty}
              />
            )}
          </DrawerContent>
        </Drawer>

        {/* DrawerInCart */}
        <Drawer
          open={Boolean(inCartProduct)}
          onOpenChange={(open) => { if (!open) setInCartProduct(null); }}
        >
          <DrawerContent
            showHandle
            className="mx-auto w-full max-w-[800px] rounded-t-2xl border-[#e2e8f0] bg-white"
          >
            {inCartProduct && (
              <DrawerInCart
                product={inCartProduct}
                productLines={inCartProductLines}
                primaryColor={primaryColor}
                onClose={() => setInCartProduct(null)}
                onEditLine={handleEditLineFromInCart}
                onChangeLineQty={changeCartLineQuantity}
                onAddNew={handleAddNewFromInCart}
              />
            )}
          </DrawerContent>
        </Drawer>

        {/* DrawerCart */}
        <Drawer open={isCartOpen} onOpenChange={setIsCartOpen}>
          <DrawerContent
            showHandle
            className="mx-auto w-full max-w-[800px] rounded-t-2xl border-[#e2e8f0] bg-white"
          >
            <DrawerCart
              cartLines={cartLines}
              cartSummary={cartSummary}
              primaryColor={primaryColor}
              onChangeLineQty={changeCartLineQuantity}
              onEditLine={handleEditLineFromCart}
              onCheckout={() => { setIsCartOpen(false); navigate(`/${tenantId}/checkout`); }}
            />
          </DrawerContent>
        </Drawer>

        <AuthDrawer
          open={showRegDrawer}
          onClose={() => setShowRegDrawer(false)}
          primaryColor={primaryColor}
          tenantId={tenantId}
          requireAddress={false}
          context="standalone"
          onAuthenticated={(customer, accessToken) => {
            if (accessToken) {
              savePersistentToken(tenantId, accessToken);
              removeSessionToken(); // rimuove vecchi token magic-link che avrebbero priorità e causerebbero guest:true
            }
            setWebappCustomer(customer || false);
            setShowRegDrawer(false);
          }}
        />

      </div>
    </div>
  );
}
